import { Endpoint } from '@business/api/endpoint';
import { HTMLService } from '@business/services';
import { FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
// import { Jodit } from 'jodit-pro';
import JoditEditor, { Jodit } from 'jodit-pro-react';
import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { InputProps } from 'reactstrap';
import { Observable } from 'rxjs';
import { FormInputProps } from './FormInput';
import './form_html.scss';
import { GlossaryService } from '@business/services/glossary_service';

export function FormHTMLInline<C extends FormControl<any>>(
  props: FormInputProps<C> &
    InputProps & {
      theme?: string;
      readonly?: boolean;
      minHeight?: number;
      media?: (text?: string) => Promise<Observable<string>>;
      autoFocus?: boolean;
      onFocus?: () => void;
      onBlur?: () => void;
      transformedFieldsValue?: Record<string, string>;
      onText?(text: string): void;
      glossarify?: boolean;
      plainText?: boolean;
      list?: any;
    },
) {
  const { autoFocus = false, onText, glossarify = false } = props;
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldValue = R.toString(fieldState.value) || '';
  const [isFocused, setIsFocused] = React.useState(false);
  const [myValue, setValue] = React.useState(fieldValue);

  useEffect(() => {
    if (myValue !== fieldValue && !isFocused) {
      setValue(fieldValue);
    }
  }, [myValue, fieldValue, isFocused]);

  const [joditKey, setJoditKey] = React.useState(R.uniqueId('jodit_'));

  const fieldReadonly = fieldConfig.readonly || false;

  const fieldPlaceholder = fieldConfig.placeholder || '';
  useEffect(() => {
    // setValue(fieldValue);
    if (!isFocused) {
      setJoditKey(R.uniqueId('jodit_'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  const handleChangeText = useCallback(
    (value: string) => {
      if (isFocused) {
        formControl.setValue(value);
        onText && onText(value);
      }
    },
    [formControl, isFocused, onText],
  );

  const handleBlur = useCallback(
    (txtValue: string) => {
      formControl.onBlur();
      props.onBlur && props.onBlur();
      formControl.setValue(txtValue);
      setTimeout(() => {
        setIsFocused(false);
      }, 250);
    },
    [formControl, props],
  );

  const handleFocus = useCallback(() => {
    formControl.onFocus();
    props.onFocus && props.onFocus();
    setIsFocused(true);
  }, [formControl, props]);

  const joditRef = useRef<Jodit | null>(null);

  const config: Partial<Jodit['options']> = useMemo(() => {
    const headers = Endpoint.dumpHeaders;
    delete headers['Content-Type'];
    delete headers['Accept'];

    return {
      // ...Jodit.defaultOptions,
      license: 'M1NDC-1DHE1-C05P6-DPFOA',
      disablePlugins: [
        'button-generator',
        'google-maps',
        'google-search',
        'page-break',
        'finder',
        'backup',
        'emoji',
        'keyboard',
        'export-docs',
        'change-case',
        'show-blocks',
        'paste-code',
      ],
      highlightSignature: {
        schema: {
          '\\{\\{([^}]+)\\}\\}': (jodit: Jodit) => {
            const color = '#FFFF0045';

            const span = jodit.createInside.element('span', {
              style: {
                backgroundColor: color,
              },
            });

            return span;
          },
        },
      },
      inline: true,
      toolbar: false,
      toolbarInlineForSelection: props.plainText ? false : true,
      placeholder: fieldPlaceholder,
      readonly: props.readonly,
      enableDragAndDropFileToEditor: true,
      saveModeInStorage: true,
      enter: props.plainText ? 'br' : 'div',
      popup: {
        a: null,
        selection: Jodit.atom([
          'paragraph',
          '|',
          'bold',
          'italic',
          'underline',
          '|',
          'font',
          'fontsize',
          'brush',
          'lineHeight',
          '|',
          'ul',
          'ol',
          '|',
          'indent',
          'outdent',
          'left',
          'center',
          'right',
          '|',
          'link',
          'table',
          '|',
          'image',
          'variables',
        ]),
      },
      uploader: {
        // ...Jodit.defaultOptions.uploader,
        headers,
        url: Endpoint.dumpUrl('@gc/folder/rte-upload'),
        insertImageAsBase64URI: false,
        imagesExtensions: ['jpg', 'png', 'jpeg', 'gif'],
        format: 'json',

        isSuccess: resp => {
          handleFocus();
          return resp && resp.success;
        },
      },

      showToolip: true,
      toolbarAdaptive: false,
      colorPickerDefaultTab: 'color',
      preset: 'inline',
      askBeforePasteHTML: false,
      defaultActionOnPaste: 'insert_clear_html',
      allowResizeX: true,
      buttons: Jodit.atom(['bold', 'italic']),
      controls: {
        variables: {
          name: 'Short Codes',
          list: props.list || {
            'h:Your Company': 'h:Your Company',
            company_name: 'Company Name',
            brand_name: 'Brand Name',
            user_name: 'User Name',
            user_email: 'User Email',
            // user_phone: 'User Phone',
            company_address: 'Address',
            company_address2: 'Address 2',
            company_city: 'City',
            company_state: 'State',
            company_country: 'Country',
            company_zip: 'Zip',
            // '|1': '|',
            'h:Your Client': 'h:Your Client',
            org_name: 'Organization Name',
            contact_first_name: 'Contact First Name',
            contact_last_name: 'Contact Last Name',
            contact_name: 'Contact Name',
            contact_email: 'Contact Email',
            contact_address: 'Address',
            contact_address2: 'Address 2',
            contact_city: 'City',
            contact_state: 'State',
            contact_zip: 'Zip Code',
            contact_country: 'Country',
            contact_website: 'Website',
            // '|2': '|',
            'h:Proposal': 'h:Proposal',
            // proposal_name: 'Name',
            total: 'Total ($)',
            date_sumitted: 'Date Submitted',
            date_accepted: 'Date Accepted',
          },
          childTemplate: (editor, key, value) => {
            if (value.startsWith('h:')) {
              return `<span style="font-weight: bold; text-align: lefer" class="${key}">${editor.i18n(value.substring(2))}</span>`;
            }
            return `<span style="font-size: 12px; text-align: left" class="${key}">${editor.i18n(value)}</span>`;
          },
          exec: function (editor, _, { control }) {
            const value = control.args && control.args.length && control.args[0];
            if (value.startsWith('h:')) return false;
            if (value.startsWith('|')) return false;

            if (value) {
              (editor as Jodit).s.insertHTML(`{{${value}}}`);
            }
          },
        },
      },
      theme: props.theme || 'default',
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      autofocus: autoFocus,
      events: {
        afterInit(instance: Jodit) {
          joditRef.current = instance;
        },
        focus() {
          handleFocus();
        },
        click() {
          handleFocus();
        },
        afterPaste() {
          // Selection-based cleanup: target only the newly pasted content
          const cleanupPastedContent = () => {
            const instance = joditRef.current;
            if (!instance || !instance.editor || !instance.selection) return;

            try {
              // Get current selection info
              const selection = window.getSelection();
              if (!selection || selection.rangeCount === 0) return;

              // Get the editor element
              const editor = instance.editor;

              // Find all strong elements in the editor
              const strongElements = editor.querySelectorAll('strong');

              strongElements.forEach((strongEl, index) => {
                const strongElement = strongEl as HTMLElement;

                // Check if this strong element is a problematic wrapper:
                // 1. It should have significant content (not just a few words)
                // 2. It should contain block elements (p, ul, etc.)
                // 3. It should be recently added (near cursor position)

                const hasBlockElements = strongElement.querySelector('p, ul, ol, li, div');
                const contentLength = strongElement.textContent?.length || 0;
                const isLikelyWrapper = hasBlockElements && contentLength > 50;

                if (isLikelyWrapper) {
                  // Move all children out of the strong element
                  const parent = strongElement.parentElement;
                  if (parent) {
                    // Move all child nodes to the parent
                    while (strongElement.firstChild) {
                      parent.insertBefore(strongElement.firstChild, strongElement);
                    }

                    // Remove the empty strong element
                    strongElement.remove();
                  }
                }
              });
            } catch (error) {
              //   console.log('Error in paste cleanup:', error);
            }
          };

          // Run cleanup with multiple timings to catch different paste scenarios
          setTimeout(cleanupPastedContent, 10);
          setTimeout(cleanupPastedContent, 50);
          setTimeout(cleanupPastedContent, 100);
        },
      },
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldPlaceholder, fieldReadonly, props.minHeight, props.list, props.theme, autoFocus]);

  const transformedFieldsValue = useMemo(() => {
    if (!props.readonly) return fieldValue;
    return fieldValue.replace(/{{([^}]+)}}/g, (match, p1) => {
      const value = props.transformedFieldsValue && props.transformedFieldsValue[p1];
      if (!value) return '';
      return value || match;
    });
  }, [fieldValue, props.readonly, props.transformedFieldsValue]);

  return (
    <div className="jodit-inline" style={{ flex: 1 }}>
      {!!props.readonly && glossarify && GlossaryService.getGlossaryContent(transformedFieldsValue)}
      {!!props.readonly && !glossarify && HTMLService.toReact(transformedFieldsValue)}
      {!props.readonly && (
        <JoditEditor key={joditKey} ref={joditRef} config={config as any} onBlur={handleBlur} onChange={handleChangeText} value={myValue} />
      )}
    </div>
  );
}
